import { useMemo, useState } from "react";
import PropTypes from 'prop-types';
import { useNavigate } from "react-router-dom";
import { Activity, Users, Calendar, TrendingUp, DollarSign, Settings as SettingsIcon, LogOut, Building2, FileText, Menu, RefreshCw, AlertCircle, BarChart3, ClipboardList } from "lucide-react";
import { ModeSelector } from "./ModeSelector";
import { useUserRole } from "../hooks/useUserRole";
import { MobileNavDrawer } from "./MobileNavDrawer";
import { useSyncStatus } from "../hooks/useSyncStatus";
import { useMode } from "@/context/ModeContext";
import { getFinancialScope } from "../utils/accessPolicy";

export default function Sidebar({ active, onLogout }) {
  const navigate = useNavigate();
  const { activeMode } = useMode();
  const { isReceptionistOnly, memberships, isLoading } = useUserRole();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { pendingCount, failedCount } = useSyncStatus();

  const financialScope = getFinancialScope(activeMode, memberships, isReceptionistOnly);
  const hideDashboard = isLoading || isReceptionistOnly;

  // All navigation items with visibility flags
  const allNavItems = [
    { id: "dashboard", label: "Dashboard", mobileLabel: "Home", icon: Activity, path: "/", requiresFinancialAccess: false },
    { id: "patients", label: "Patients", mobileLabel: "Patients", icon: Users, path: "/patients", requiresFinancialAccess: false },
    { id: "appointments", label: "Appointments", mobileLabel: "Appts", icon: Calendar, path: "/appointments", requiresFinancialAccess: false },
    { id: "clinics", label: "My Clinics", mobileLabel: "Clinics", icon: Building2, path: "/clinics", requiresFinancialAccess: false },
    { id: "invoices", label: "Invoices", mobileLabel: "Invoices", icon: FileText, path: "/invoices", requiresFinancialAccess: false },
    { id: "referrals", label: "Associated Doctors", mobileLabel: "Associates", icon: TrendingUp, path: "/referrals", requiresFinancialAccess: true },
    { id: "facility_charges", label: "Facility Charges", mobileLabel: "Charges", icon: DollarSign, path: "/facility-charges", requiresFinancialAccess: true },
    { id: "reports", label: "Reports", mobileLabel: "Reports", icon: BarChart3, path: "/reports", requiresFinancialAccess: true },
    { id: "custom-forms", label: "Custom Forms", mobileLabel: "Forms", icon: ClipboardList, path: "/custom-forms", requiresFinancialAccess: false, clinicalOnly: true },
    { id: "settings", label: "Settings", mobileLabel: "Settings", icon: SettingsIcon, path: "/settings", requiresFinancialAccess: false },
  ];

  // Filter items based on role & active mode
  const navItems = useMemo(() => {
    let roleVisibleItems = isReceptionistOnly
      ? allNavItems.filter(item => !item.clinicalOnly)
      : allNavItems;

    if (hideDashboard) {
      roleVisibleItems = roleVisibleItems.filter(item => item.id !== 'dashboard');
    }

    if (!financialScope.canAccessFinancials) {
      return roleVisibleItems.filter(item => !item.requiresFinancialAccess);
    }
    return roleVisibleItems;
  }, [financialScope.canAccessFinancials, isReceptionistOnly, hideDashboard]);

  // Mobile bottom bar: Show only 4 primary items + More button
  // For receptionist: patients, appointments, invoices, clinics
  // For doctors/admins: dashboard, patients, appointments, invoices
  const mobileVisibleIds = isReceptionistOnly
    ? ['patients', 'appointments', 'invoices', 'clinics']
    : ['dashboard', 'patients', 'appointments', 'invoices'];

  const mobileVisibleItems = navItems.filter(item => mobileVisibleIds.includes(item.id));

  return (
    <>
      <div className="sidebar" data-testid="sidebar">
        <div className="sidebar-logo">
          <h1>
            <Activity size={28} />
            ClinicNode
          </h1>
        </div>

        <div className="px-3 mb-4 hidden md:block">
          <ModeSelector />
        </div>

        {/* Sync Status Banner */}
        {(pendingCount > 0 || failedCount > 0) && (
          <div className="px-3 mb-3 hidden md:block">
            <div className="text-xs space-y-1">
              {pendingCount > 0 && (
                <div className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  <RefreshCw size={12} className="animate-spin" />
                  <span>{pendingCount} syncing...</span>
                </div>
              )}
              {failedCount > 0 && (
                <div className="flex items-center gap-1.5 text-red-600 bg-red-50 px-2 py-1 rounded">
                  <AlertCircle size={12} />
                  <span>{failedCount} failed to sync</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Desktop Navigation - all items */}
        <nav className="desktop-nav">
          {navItems.map((item) => (
            <div
              key={item.id}
              data-testid={`nav-${item.id}`}
              className={`nav-item ${active === item.id ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <item.icon size={18} className="nav-icon" />
              <span className="nav-label-desktop">{item.label}</span>
              <span className="nav-label-mobile">{item.mobileLabel}</span>
            </div>
          ))}
          <div
            data-testid="nav-logout"
            className="nav-item logout-item"
            onClick={onLogout}
          >
            <LogOut size={18} className="nav-icon" />
            <span className="nav-label-desktop">Logout</span>
            <span className="nav-label-mobile">Logout</span>
          </div>
        </nav>

        {/* Mobile Navigation - limited items + More */}
        <nav className="mobile-nav">
          {mobileVisibleItems.map((item) => (
            <div
              key={item.id}
              data-testid={`nav-mobile-${item.id}`}
              className={`nav-item ${active === item.id ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <item.icon size={18} className="nav-icon" />
              <span className="nav-label-mobile">{item.mobileLabel}</span>
            </div>
          ))}
          <div
            data-testid="nav-more"
            className="nav-item"
            onClick={() => setIsDrawerOpen(true)}
          >
            <Menu size={18} className="nav-icon" />
            <span className="nav-label-mobile">More</span>
          </div>
        </nav>
      </div>

      {/* Mobile Drawer with full navigation */}
      <MobileNavDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        navItems={navItems}
        active={active}
        onLogout={onLogout}
      />
    </>
  );
}

Sidebar.propTypes = {
  active: PropTypes.string,
  onLogout: PropTypes.func
};